import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import TerimaBayar from '../../../models/TerimaBayar';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { buttonTheme, getPathObject, PageContainerActionParamType, pageToolbarDetailHeight, STATUS_BOOK_SERV_NEW_NAME } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import TerimaBayarTransaksi from '../../../models/TerimaBayarTransaksi';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import FormModal from '../../../components/FormModal';
import { Box, Button, ButtonGroup, SvgIconOwnProps, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import { v4 as uuidv4, validate as uuidValidate } from "uuid";
import NotificationDialog from '../../../components/NotificationDialog';
import GridViewEdit from '../../../components/GridViewEdit';
import Customer from '../../../models/Customer';
import { LookupSingleProp } from '../../../models/Lookup';
import moment from 'moment';
import Workorder from '../../../models/Workorder';

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Transaksi";
const code: MenuCode = "SERVICE-TERIMA-BAYAR";
const title: MenuTitle = "Terima Bayar";
const endpoint = "terima-bayar";
    
const gridIconStyle : SvgIconOwnProps = {
    fontSize: "small",
    sx: {marginRight: "0.1rem", paddingBottom: 0},
};

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<TerimaBayar>({tanggal_transaksi: moment().format("YYYY-MM-DD")});
    const [initialBookingServiceKendaraanList, setInitialBookingServiceKendaraanList] = useState<TerimaBayarTransaksi[]>([]);
    const [detailKendaraanToEdit, setDetailKendaraanToEdit] = useState<TerimaBayarTransaksi | null>(null);
    const [workorderToView, setWorkorderToView] = useState<Workorder | null>(null);
    const [validationResult, setValidationResult] = useState<any[] | null>(null);
    const navigate = useNavigate();
    const customerRef = useRef();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: DataFormFieldChangeParamType<TerimaBayar>) => {
        const {fieldName, value, keyFieldName, name, isObject} = valueObj;

        if (isObject && name && keyFieldName)
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : undefined,
                [name]: value,
            });
        
        if (!isObject)
            setFormData({
                ...formData,
                [fieldName]: value,
            });
    }, [formData]);

    const onDetailChanged = useCallback((detailData?: TerimaBayarTransaksi) => {
        if (!detailData)
            return;

        const { list_terima_bayar_transaksi } = formData;
        const current_list_terima_bayar_transaksi = list_terima_bayar_transaksi ?? [];
    }, [formData, apiCaller]);

    const onValidate = useCallback((data: TerimaBayar | undefined) => {
        if (!data?.tanggal_transaksi || !data?.ucode_customer)
            return false;

        if (data?.ucode && !data.no_transaksi)
            return false;

        return true;
    }, []);

    const onValidateDetailKendaraan = useCallback((detailToCheck: TerimaBayarTransaksi, callback: Function) => {
        const existingData = formData.list_terima_bayar_transaksi?.filter(d => d.ucode_transaksi === detailToCheck.ucode_transaksi && (detailToCheck.is_new || d.ucode !== detailToCheck.ucode));

        if (existingData?.length)
        {
            setValidationResult([
                {
                    type: "Error",
                    message: "No transaksi tidak boleh kembar.",
                }
            ]);

            return;
        }

        callback(detailToCheck);
    }, [formData]);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<TerimaBayar> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const deletedBookingServiceKendaraanList: string[] = [];

        initialBookingServiceKendaraanList.forEach(b => {
            if (param?.data.list_terima_bayar_transaksi?.filter(d => d.ucode === b.ucode)?.length || !b.ucode)
                return;

            deletedBookingServiceKendaraanList.push(b.ucode);
        });

        const dataToSend: TerimaBayar = {
            ucode: param.data.ucode,
            no_transaksi: param.data.no_transaksi,
            tanggal_transaksi: moment(param.data.tanggal_transaksi).format("YYYY-MM-DD"),
            ucode_divisi: param.data.ucode_divisi,
            ucode_customer: param.data.ucode_customer,
            keterangan: param.data.keterangan,
        };

        const apiCallerObj = dataToSend.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(dataToSend)) : apiCaller.post(`${endpoint}`, JSON.stringify(dataToSend));

        apiCallerObj
            .then((response) => {
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, initialBookingServiceKendaraanList]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<TerimaBayar>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    isViewMode={isViewMode}
                    fields={[
                        {
                            name: "no_transaksi",
                            title: "No. Transaksi",
                            inputType: "text",
                            dataType: (typeof formData.no_transaksi),
                            isReadonly: true,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<TerimaBayar>) => onChanged(valueObj),
                        },
                        {
                            name: "tanggal_transaksi",
                            title: "Tanggal Transaksi",
                            inputType: "date",
                            dataType: (typeof formData.tanggal_transaksi),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<TerimaBayar>) => onChanged(valueObj),
                        },
                        {
                            name: "customer",
                            keyField: "ucode",
                            labelField: "nama",
                            editKeyField: "ucode_customer",
                            title: "Customer",
                            inputType: "combobox",
                            dataType: (typeof Customer),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<TerimaBayar>) => onChanged(valueObj),
                            searchEndpoint: "customer/nama",
                            searchEndpointFilterParamName: "nama",
                            ref: customerRef,
                        },
                        {
                            name: "keterangan_operator",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<TerimaBayar>) => onChanged(valueObj),
                            rows: 3,
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarDetailHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Kendaraan</Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                                <Tooltip
                                    title="Tambah Detail Barang"
                                    arrow
                                >
                                    <Button variant="contained" color="primary" size="small" onClick={() => setDetailKendaraanToEdit({ is_new: true })} disabled={isViewMode}>
                                        <AddIcon {...gridIconStyle} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </ThemeProvider>
                    </Box>
                </Box>
                <GridViewEdit<TerimaBayarTransaksi>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: TerimaBayarTransaksi) => {},
                                    icon: EditIcon,
                                    variant: "outlined",
                                },
                                {
                                    title: "Hapus",
                                    color: "delete",
                                    action: (rowData: TerimaBayarTransaksi) => {},
                                    icon: DeleteIcon,
                                    variant: "outlined",
                                }
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "nopol",
                            columnTitle: "No. Polisi",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "jenis_kendaraan",
                            columnTitle: "Jenis Kendaraan",
                            columnType: "data",
                        },
                        {
                            columnName: "status_service_kendaraan",
                            columnTitle: "Status",
                            columnType: "data",
                        },
                    ]}
                    data={formData.list_terima_bayar_transaksi}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
            </FormContainer>
        {
            detailKendaraanToEdit ?
            <FormModal<TerimaBayarTransaksi>
                apiCaller={apiCaller}
                data={detailKendaraanToEdit}
                title={detailKendaraanToEdit.is_new ? "Tambah Detail Kendaraan" : "Ubah Detail Kendaraan"}
                fields={[
                    {
                        name: "nopol",
                        title: "No. Polisi",
                        inputType: "text",
                        dataType: (typeof detailKendaraanToEdit.no_transaksi),
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 6,
                    },
                ]}
                onClose={() => setDetailKendaraanToEdit(null)}
                onSave={(detailData?: TerimaBayarTransaksi) => {
                    onDetailChanged(detailData);
                    setDetailKendaraanToEdit(null);
                }}
                onValidate={(detailToCheck: TerimaBayarTransaksi, callback: Function) => {
                    onValidateDetailKendaraan(detailToCheck, callback);
                }}
            />
            :
            null
        }
        {
            validationResult?.length ?
            <NotificationDialog
                message={validationResult[0].message}
                onClose={() => setValidationResult(null)}
                title="Error!"
            />
            :
            null
        }
        </>
    );
};

export default Form;
